import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert";
import { useParams, useNavigate } from "react-router-dom";
import {
  Upload,
  X,
  Image,
  Package,
  AlertCircle,
  Crown,
  Video,
  Film,
  CheckCircle,
  Loader,
} from "lucide-react";
import { DOMAIN } from "../../strings/CONSTANTS";

// ─── Types ───────────────────────────────────────────────────────────────────
type UploadState = "idle" | "uploading" | "done" | "error";

interface VideoConvertResult {
  url: string;
  sizeKB: number;
  previewUrl: string;
}

interface ExistingSticker {
  url: string;
  isRemoved?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
const CreateStickerPack: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const videoInputRef = useRef<HTMLInputElement>(null);

  const [isEdit, setIsEdit] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [categoryId, setCategoryId] = useState("");
  const [title, setTitle] = useState("");
  const [mainImageBase64, setMainImageBase64] = useState("");
  const [mainImagePreview, setMainImagePreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [sendNotification, setSendNotification] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [isAnimated, setIsAnimated] = useState(false);

  // 🛡️ SEPARATED STATE TO PREVENT UNINTENDED STICKER DELETIONS
  const [existingStickers, setExistingStickers] = useState<ExistingSticker[]>([]);
  const [newStickerImages, setNewStickerImages] = useState<string[]>([]);
  const [newStickerPreviews, setNewStickerPreviews] = useState<string[]>([]);

  const [convertedStickers, setConvertedStickers] = useState<VideoConvertResult[]>([]);
  const [videoUploads, setVideoUploads] = useState<
    { name: string; state: UploadState; error?: string }[]
  >([]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${DOMAIN}/category/list`);
      setCategories(res.data.data || []);
    } catch (err) {
      console.error("Category fetch error:", err);
    }
  };

  const loadPackForEdit = async () => {
    try {
      const res = await axios.get(`${DOMAIN}/sticker/${id}`);
      if (res.data.success) {
        const pack = res.data.pack;
        setIsEdit(true);
        setCategoryId(pack.categoryId._id);
        setTitle(pack.title);
        setMainImagePreview(pack.mainImage);
        
        // Store stickers with clean relative paths
        const stickersWithState: ExistingSticker[] = (pack.stickers || []).map((url: string) => ({
          url, 
          isRemoved: false,
        }));
        setExistingStickers(stickersWithState);
        
        console.log("📋 Loaded existing stickers:", stickersWithState.map((s: ExistingSticker) => s.url));
        
        setNewStickerImages([]);
        setNewStickerPreviews([]);
        setConvertedStickers([]);
        setIsPremium(pack.isPremium || false);
        setIsAnimated(pack.type === "animated");
      }
    } catch (err) {
      console.log("Error loading pack:", err);
      Swal("Error", "Failed to load sticker pack for editing", "error");
    }
  };

  useEffect(() => {
    fetchCategories();
    if (id) loadPackForEdit();
  }, [id]);

  const handleMainImage = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const validExtensions = [".png", ".jpg", ".jpeg", ".webp"];
    if (!validExtensions.some((ext) => fileName.endsWith(ext))) {
      Swal("Invalid Format", "Main image must be PNG, JPG, JPEG, or WEBP.", "error");
      return;
    }
    if (file.size > 200 * 1024) {
      Swal("File Too Large", `Main image is ${(file.size / 1024).toFixed(0)}KB. Max 200KB.`, "error");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setMainImageBase64(reader.result?.toString().split(",")[1] || "");
      setMainImagePreview(reader.result?.toString() || "");
    };
    reader.readAsDataURL(file);
  };

  const handleStickerImages = (e: any) => {
    const files: File[] = Array.from(e.target.files);
    
    // Calculate active stickers (not removed)
    const activeExistingCount = existingStickers.filter(s => !s.isRemoved).length;
    const total = activeExistingCount + newStickerPreviews.length + convertedStickers.length + files.length;

    if (total > 30) {
      Swal("Limit Exceeded", "Maximum 30 stickers per pack.", "error");
      return;
    }

    files.forEach((file) => {
      if (file.size === 0) {
        console.warn("Skipping empty file:", file.name);
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result?.toString().split(",")[1] || "";
        if (base64.length > 0) {
          setNewStickerImages((prev) => [...prev, base64]);
          setNewStickerPreviews((prev) => [...prev, reader.result?.toString() || ""]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  // 🔴 REMOVE EXISTING STICKER (Edit Mode)
  const toggleRemoveExistingSticker = (urlToToggle: string) => {
    setExistingStickers((prev: ExistingSticker[]) =>
      prev.map((sticker: ExistingSticker) =>
        sticker.url === urlToToggle
          ? { ...sticker, isRemoved: true }
          : sticker
      )
    );
  };

  // 🗑️ REMOVE NEWLY ADDED STICKER
  const removeNewSticker = (index: number) => {
    setNewStickerPreviews((prev) => prev.filter((_, i) => i !== index));
    setNewStickerImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleVideoFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files: File[] = Array.from(e.target.files || []);
    if (!files.length) return;

    const activeExistingCount = existingStickers.filter((s: ExistingSticker) => !s.isRemoved).length;
    const totalAfter = activeExistingCount + newStickerPreviews.length + convertedStickers.length + files.length;
    
    if (totalAfter > 30) {
      Swal("Limit Exceeded", "Maximum 30 stickers per pack.", "error");
      return;
    }

    const allowedTypes = ["video/mp4", "video/quicktime", "video/x-msvideo", "video/webm", "video/mpeg"];
    for (const file of files) {
      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp4|mov|avi|webm|mpeg)$/i)) {
        Swal("Invalid Format", `"${file.name}" — please upload MP4, MOV, AVI, or WEBM.`, "error");
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        Swal("File Too Large", `"${file.name}" exceeds 50MB limit.`, "error");
        return;
      }
    }

    const initialRows = files.map((f) => ({ name: f.name, state: "uploading" as UploadState }));
    setVideoUploads((prev) => [...prev, ...initialRows]);
    const startIndex = videoUploads.length;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const rowIndex = startIndex + i;

      try {
        const formData = new FormData();
        formData.append("file", file);

        const res = await axios.post(`${DOMAIN}/sticker/video-to-gif`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.data.success) {
          const hostBase = DOMAIN.endsWith('/api') ? DOMAIN.substring(0, DOMAIN.length - 4) : DOMAIN;
          const cleanUrlPath = res.data.url.startsWith('/api') ? res.data.url.substring(4) : res.data.url;

          const result: VideoConvertResult = {
            url: res.data.url,
            sizeKB: res.data.sizeKB,
            previewUrl: cleanUrlPath.startsWith('http') ? cleanUrlPath : `${hostBase}${cleanUrlPath}`,
          };
          
          setConvertedStickers((prev) => [...prev, result]);
          setVideoUploads((prev) =>
            prev.map((row, idx) => (idx === rowIndex ? { ...row, state: "done" } : row))
          );
        } else {
          throw new Error(res.data.message || "Conversion failed");
        }
      } catch (err: any) {
        const msg = err?.response?.data?.message || err?.message || "Upload failed";
        setVideoUploads((prev) =>
          prev.map((row, idx) =>
            idx === rowIndex ? { ...row, state: "error", error: msg } : row
          )
        );
      }
    }

    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  const removeConvertedSticker = (index: number) => {
    setConvertedStickers((prev) => prev.filter((_, i) => i !== index));
  };

  const savePack = async () => {
    if (!categoryId || !title || (!mainImagePreview && !mainImageBase64)) {
      Swal("Error", "Category, title and cover image are all required.", "error");
      return;
    }

    const activeExistingCount = existingStickers.filter((s: ExistingSticker) => !s.isRemoved).length;
    const totalActiveCount = activeExistingCount + newStickerImages.length + convertedStickers.length;
    
    if (totalActiveCount < 3) {
      Swal(
        "Minimum Stickers Required",
        `Pack must contain at least 3 stickers. Currently you have ${totalActiveCount} active sticker${totalActiveCount !== 1 ? 's' : ''}.`,
        "error"
      );
      return;
    }

    setLoading(true);

    try {
      let finalStickerImagesPayload = [...newStickerImages];
      
      for (let i = 0; i < convertedStickers.length; i++) {
        const sticker = convertedStickers[i];
        try {
          const response = await fetch(sticker.previewUrl);
          if (!response.ok) throw new Error(`Fetch err: ${response.status}`);
          const blob = await response.blob();
          
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result?.toString().split(",")[1] || "");
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          
          if (base64) finalStickerImagesPayload.push(base64);
        } catch (err) {
          console.error("Failed parsing newly added video stream link asset item", err);
        }
      }

      // 🛡️ RE-ENGINEERED REGEX PARSER: Extracts the subfolder path without domain prefix comparison conflicts
      const activeExistingUrls = existingStickers
        .filter((s: ExistingSticker) => !s.isRemoved)
        .map((s: ExistingSticker) => {
          const match = s.url.match(/(\/stickers\/items\/[^\s?#]+)/);
          return match ? match[1] : s.url.replace(DOMAIN, "");
        });

      const payload: any = {
        categoryId,
        title,
        mainImage: mainImageBase64 || null,
        stickerImages: finalStickerImagesPayload,
        sendNotification,
        isPremium,
        type: isAnimated ? "animated" : "static",
      };

      if (isEdit) {
        payload.stickers = activeExistingUrls;
      }

      const res = isEdit
        ? await axios.put(`${DOMAIN}/sticker/update/${id}`, payload)
        : await axios.post(`${DOMAIN}/sticker/create`, payload);

      if (res.data.success) {
        Swal(
          "Success",
          isEdit ? "Sticker pack updated successfully!" : "Sticker pack created successfully!",
          "success"
        );
        navigate("/sticker-categories");
      } else {
        throw new Error(res.data.message || "Save failed");
      }
    } catch (err: any) {
      Swal("Error", err?.response?.data?.message || err?.message || "Operation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const activeExistingCount = existingStickers.filter((s: ExistingSticker) => !s.isRemoved).length;
  const totalActiveCount = activeExistingCount + newStickerPreviews.length + convertedStickers.length;
  const anyUploading = videoUploads.some((v) => v.state === "uploading");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Package className="w-6 h-6 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800">
              {isEdit ? "Edit Sticker Pack" : "Create New Sticker Pack"}
            </h1>
          </div>
          <p className="text-slate-600 ml-14">
            {isEdit ? "Update your sticker pack details" : "Add a new sticker pack to your collection"}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="p-8 space-y-6">

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Category</label>
              <select
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all bg-white text-slate-700"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">Select a category</option>
                {categories.map((cat: any) => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Sticker Pack Title</label>
              <input
                type="text"
                placeholder="Enter pack title"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
              <div className="flex items-center gap-3">
                <Crown className="w-5 h-5 text-purple-600" />
                <label className="text-sm font-semibold text-purple-700">Premium Sticker Pack</label>
                <input
                  type="checkbox"
                  className="w-5 h-5 accent-purple-600 cursor-pointer"
                  checked={isPremium}
                  onChange={(e) => setIsPremium(e.target.checked)}
                />
              </div>
              <p className="text-xs text-slate-500 mt-2 ml-8">Mark this pack as premium content</p>
            </div>

            <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
              <div className="flex items-center gap-3">
                <Video className="w-5 h-5 text-blue-600" />
                <label className="text-sm font-semibold text-blue-700">Animated Sticker Pack</label>
                <input
                  type="checkbox"
                  className="w-5 h-5 accent-blue-600 cursor-pointer"
                  checked={isAnimated}
                  onChange={(e) => {
                    setIsAnimated(e.target.checked);
                    if (!e.target.checked) setConvertedStickers([]);
                  }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-2 ml-8">
                Enable for animated stickers.{" "}
                <span className="text-blue-600 font-medium">
                  Upload GIF files or convert MP4/MOV videos automatically.
                </span>
              </p>
            </div>

            <div className="flex items-center gap-3">
              <label className="text-sm font-semibold text-slate-700">Send Notification To Users</label>
              <input
                type="checkbox"
                className="w-5 h-5 accent-indigo-600 cursor-pointer"
                checked={sendNotification}
                onChange={(e) => setSendNotification(e.target.checked)}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Main Cover Image (Tray Icon)
              </label>
              <div className="flex items-start gap-4">
                <label className="flex-1 cursor-pointer">
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 hover:border-indigo-400 hover:bg-indigo-50 transition-all">
                    <div className="flex flex-col items-center gap-2">
                      <div className="p-3 bg-indigo-100 rounded-full">
                        <Image className="w-6 h-6 text-indigo-600" />
                      </div>
                      <p className="text-sm font-medium text-slate-700">Click to upload cover image</p>
                      <p className="text-xs text-slate-500">PNG, JPG, WEBP — max 200KB</p>
                    </div>
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleMainImage}
                    className="hidden"
                  />
                </label>
                {mainImagePreview && (
                  <div className="relative group">
                    <img
                      src={mainImagePreview}
                      className="w-32 h-32 rounded-lg border-2 border-slate-200 object-cover shadow-sm"
                      alt="Preview"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => { setMainImagePreview(""); setMainImageBase64(""); }}
                        className="p-1.5 bg-red-500 rounded-full hover:bg-red-600"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-semibold text-slate-700">
                  Sticker Images Group
                  <span className="ml-2 text-xs font-normal text-slate-500">
                    ({totalActiveCount}/30 stickers)
                  </span>
                </label>
                {totalActiveCount < 3 && (
                  <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1 rounded-lg">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-xs font-medium">
                      Need {3 - totalActiveCount} more sticker{3 - totalActiveCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>

              <div className={`grid gap-4 ${isAnimated ? "grid-cols-2" : "grid-cols-1"}`}>
                <label className="cursor-pointer block">
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-5 hover:border-indigo-400 hover:bg-indigo-50 transition-all h-full">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <div className="p-3 bg-indigo-100 rounded-full">
                        <Upload className="w-5 h-5 text-indigo-600" />
                      </div>
                      <p className="text-sm font-medium text-slate-700">
                        {isAnimated ? "Upload GIF files" : "Upload sticker images"}
                      </p>
                      <p className="text-xs text-slate-500">
                        {isAnimated
                          ? "GIF only — max 3MB each"
                          : "PNG, JPG, JPEG, WEBP, GIF — max 500KB each"}
                      </p>
                    </div>
                  </div>
                  <input
                    type="file"
                    accept={isAnimated ? "image/gif" : "image/png,image/jpeg,image/jpg,image/webp,image/gif"}
                    multiple
                    onChange={handleStickerImages}
                    className="hidden"
                  />
                </label>

                {isAnimated && (
                  <label className="cursor-pointer block">
                    <div className="border-2 border-dashed border-orange-300 rounded-lg p-5 hover:border-orange-400 hover:bg-orange-50 transition-all h-full">
                      <div className="flex flex-col items-center gap-2 text-center">
                        <div className="p-3 bg-orange-100 rounded-full">
                          <Film className="w-5 h-5 text-orange-600" />
                        </div>
                        <p className="text-sm font-medium text-slate-700">Convert Video → GIF</p>
                        <p className="text-xs text-slate-500">
                          MP4, MOV, AVI, WEBM — max 50MB
                        </p>
                        <p className="text-xs text-orange-600 font-medium">
                          Auto-trims to 3s · 512×512 · ≤500KB
                        </p>
                      </div>
                    </div>
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/mpeg"
                      multiple
                      onChange={handleVideoFiles}
                      className="hidden"
                    />
                  </label>
                )}
              </div>

              {videoUploads.length > 0 && (
                <div className="mt-3 space-y-2">
                  {videoUploads.map((v, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm border ${
                        v.state === "done"
                          ? "bg-green-50 border-green-200"
                          : v.state === "error"
                          ? "bg-red-50 border-red-200"
                          : "bg-orange-50 border-orange-200"
                      }`}
                    >
                      {v.state === "uploading" && (
                        <Loader className="w-4 h-4 text-orange-500 animate-spin shrink-0" />
                      )}
                      {v.state === "done" && (
                        <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                      )}
                      {v.state === "error" && (
                        <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                      )}
                      <span className="truncate flex-1 text-slate-700">{v.name}</span>
                      {v.state === "uploading" && (
                        <span className="text-xs text-orange-600 shrink-0">Converting…</span>
                      )}
                      {v.state === "error" && (
                        <span className="text-xs text-red-600 shrink-0 max-w-[200px] truncate">
                          {v.error}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {totalActiveCount > 0 && (
                <div className="mt-4">
                  <div className="grid grid-cols-6 sm:grid-cols-8 gap-3 border rounded-xl p-4 bg-slate-50">
                    {/* 1. Existing database images - ONLY RENDER ACTIVE ONES */}
                    {existingStickers
                      .filter((sticker) => !sticker.isRemoved)
                      .map((sticker, index) => (
                        <div key={`existing-${index}`} className="relative group border border-slate-200 rounded-lg p-1 bg-white">
                          <img
                            src={sticker.url.startsWith("http") ? sticker.url : `${DOMAIN}${sticker.url}`}
                            className="w-full aspect-square object-contain"
                            alt=""
                          />
                          <button
                            type="button"
                            onClick={() => toggleRemoveExistingSticker(sticker.url)}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}

                    {/* 2. Newly Uploaded Static Images / GIFs */}
                    {newStickerPreviews.map((img, index) => (
                      <div key={`new-${index}`} className="relative group border border-dashed border-indigo-400 rounded-lg p-1 bg-white">
                        <img
                          src={img}
                          className="w-full aspect-square object-contain"
                          alt=""
                        />
                        <button
                          type="button"
                          onClick={() => removeNewSticker(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}

                    {/* 3. Converted Dynamic Video Clips */}
                    {convertedStickers.map((s, index) => (
                      <div key={`vid-${index}`} className="relative group border border-orange-400 rounded-lg p-1 bg-white">
                        <img
                          src={s.previewUrl}
                          className="w-full aspect-square object-contain"
                          alt=""
                        />
                        <span className="absolute bottom-0 left-0 right-0 text-center text-[9px] bg-orange-500 text-white rounded-b-lg py-0.5">
                          {s.sizeKB}KB
                        </span>
                        <button
                          type="button"
                          onClick={() => removeConvertedSticker(index)}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state alert banner for edit context */}
              {isEdit && totalActiveCount === 0 && (
                <div className="text-center py-8 bg-amber-50 rounded-lg border border-amber-200 mt-4">
                  <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-2" />
                  <p className="text-amber-700 font-medium">No active stickers in this pack</p>
                  <p className="text-amber-600 text-sm mt-1">
                    You need at least 3 stickers. Please upload some images or transcode video clips.
                  </p>
                </div>
              )}
            </div>

          </div>

          <div className="bg-slate-50 px-8 py-4 border-t border-slate-200 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate("/sticker-categories")}
              className="px-5 py-2.5 text-slate-700 font-medium hover:bg-slate-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={savePack}
              disabled={loading || anyUploading}
              className="px-6 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {anyUploading ? (
                <span className="flex items-center gap-2">
                  <Loader className="animate-spin h-4 w-4" />
                  Converting videos…
                </span>
              ) : loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {isEdit ? "Updating…" : "Creating…"}
                </span>
              ) : (
                <span>{isEdit ? "Update Pack" : "Create Pack"}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateStickerPack;